/** @format */

import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Loader2,
  Package,
  AlertCircle,
  Sparkles,
  TrendingUp,
  Award,
  Building2,
  X,
  Filter,
} from "lucide-react";
import { ProductInterface } from "@/types/product";
import { FilterCategoryOptions } from "@/app/(main)/danh-muc/[slug]/DanhmucClient";
import ProductWrapperCard from "../ui/product/product-wrapper-card";

// Types
export interface ApiResponseProductScroll {
  products: ProductInterface[];
  hasMore: boolean;
  nextPage?: number;
  total?: number;
}
interface FilterState {
  minPrice: number;
  maxPrice: number;
  categoryTypeSlug: string;
}

interface ScrollToLoadProductsProps {
  fetchProducts: (
    page: number,
    limit: number,
    filterOptions?: FilterCategoryOptions
  ) => Promise<ApiResponseProductScroll>;
  itemsPerPage?: number;
  loadingComponent?: React.ReactNode;
  errorComponent?: (error: string, retry: () => void) => React.ReactNode;
  renderProduct?: (product: ProductInterface, index: number) => React.ReactNode;
  containerClassName?: string;
  gridClassName?: string;
  loadOffset?: number;
  onProductsLoaded?: (
    products: ProductInterface[],
    totalLoaded: number
  ) => void;
  onError?: (error: string) => void;
  categoryName?: string;
}

export const ScrollToLoadProductsWithCategory: React.FC<
  ScrollToLoadProductsProps
> = ({
  fetchProducts,
  itemsPerPage = 8,
  loadingComponent,
  errorComponent,
  renderProduct,
  containerClassName = "",
  gridClassName = "",
  loadOffset = 100,
  categoryName = "Sản phẩm",
  onProductsLoaded,
  onError,
}) => {
  const [products, setProducts] = useState<ProductInterface[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [initialLoad, setInitialLoad] = useState(true);
  const [selectedPriceRange, setSelectedPriceRange] = useState<string>("");

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState<FilterCategoryOptions>({
    minPrice: 0,
    maxPrice: 10000000,
    categoryTypeSlug: "guitar-acoustic",
  });
  const [tempFilters, setTempFilters] = useState<FilterCategoryOptions>({
    minPrice: 0,
    maxPrice: 10000000,
    categoryTypeSlug: "guitar-acoustic",
  });

  // Add debounced price inputs for better UX
  const [debouncedMinPrice, setDebouncedMinPrice] = useState("₫0");
  const [debouncedMaxPrice, setDebouncedMaxPrice] = useState("₫10,000,000");

  const observerRef = useRef<HTMLDivElement>(null);
  const loadingRef = useRef(false);

  // Function to format Vietnamese currency
  const formatVND = (amount: number): string => {
    return new Intl.NumberFormat("vi-VN", {
      style: "currency",
      currency: "VND",
    }).format(amount);
  };

  // Function to parse VND string back to number
  const parseVND = (value: string): number => {
    const cleaned = value.replace(/[^\d]/g, "");
    return cleaned ? parseInt(cleaned, 10) : 0;
  };

  const loadProducts = useCallback(
    async (page: number, reset = false) => {
      if (loadingRef.current) return;

      loadingRef.current = true;
      setLoading(true);
      setError(null);

      try {
        const response = await fetchProducts(
          page,
          itemsPerPage,
          categoryName.includes("Sản phẩm") ? filters : undefined
        );
        setProducts((prev) =>
          reset ? response.products : [...prev, ...response.products]
        );
        setHasMore(response.hasMore);
        setCurrentPage(page);

        const totalLoaded = reset
          ? response.products.length
          : products.length + response.products.length;
        onProductsLoaded?.(response.products, totalLoaded);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to load products";
        setError(errorMessage);
        onError?.(errorMessage);
      } finally {
        setLoading(false);
        loadingRef.current = false;
        setInitialLoad(false);
      }
    },
    [
      fetchProducts,
      itemsPerPage,
      products.length,
      onProductsLoaded,
      onError,
      filters,
    ]
  );

  useEffect(() => {
    loadProducts(1, true);
  }, []);

  useEffect(() => {
    // Reset state when filters change

    setProducts([]);
    setCurrentPage(1);
    setHasMore(true);
    setError(null);
    loadProducts(1, true);
  }, [filters]); // Add filters as dependency

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target.isIntersecting && !loading && hasMore && !error) {
          loadProducts(currentPage + 1);
        }
      },
      {
        threshold: 0.1,
        rootMargin: `${loadOffset}px`,
      }
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => observer.disconnect();
  }, [loadProducts, loading, hasMore, error, currentPage, loadOffset]);

  const handleOpenFilter = () => {
    setTempFilters(filters);
    setDebouncedMinPrice(formatVND(filters.minPrice));
    setDebouncedMaxPrice(formatVND(filters.maxPrice));
    setIsFilterOpen(true);
  };

  const handleCloseFilter = () => {
    setIsFilterOpen(false);
  };

  const handleApplyFilters = () => {
    setFilters(tempFilters);
    setIsFilterOpen(false);
    // console.log("TEMP FILTER", tempFilters);
    // fetchProducts(1, itemsPerPage, tempFilters);
    // You can add filter logic here
  };

  const handleClearFilters = () => {
    const defaultFilters = {
      minPrice: 0,
      maxPrice: 10000000,
      categoryTypeSlug: "",
    };
    setTempFilters(defaultFilters);
    setFilters(defaultFilters); // This will trigger the useEffect
    setDebouncedMinPrice("₫0");
    setDebouncedMaxPrice("₫10,000,000");
    setIsFilterOpen(false);
    // Remove the manual reset and loadProducts call since useEffect will handle it
  };

  // Debounced price change handlers

  const handlePriceRangeChange = (value: string) => {
    setSelectedPriceRange(value);

    // Parse the range and update your filters
    const [min, max] = value.split("-");
    // Update the debounced price states
    setDebouncedMinPrice(min);
    setDebouncedMaxPrice(max);

    // Update your filter state with the min and max values
    // Example: setFilters({ ...filters, minPrice: min, maxPrice: max });
  };

  const handlePriceInputChange = useCallback(
    (type: "min" | "max", value: string) => {
      if (type === "min") {
        setDebouncedMinPrice(value);
      } else {
        setDebouncedMaxPrice(value);
      }
    },
    []
  );

  // Debounce effect for price updates
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const minPrice = parseVND(debouncedMinPrice);
      const maxPrice = parseVND(debouncedMaxPrice);
      setTempFilters((prev) => ({
        ...prev,
        minPrice,
        maxPrice,
      }));
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [debouncedMinPrice, debouncedMaxPrice]);

  const handleTypeSelect = (type: string) => {
    // Toggle selection: if same type is clicked, deselect it
    const newType = tempFilters.categoryTypeSlug === type ? "" : type;
    setTempFilters({ ...tempFilters, categoryTypeSlug: newType });
  };

  const hasActiveFilters =
    filters.minPrice > 0 ||
    filters.maxPrice < 10000000 || // Updated comparison
    filters.categoryTypeSlug !== ""; // Updated condition

  const retry = useCallback(() => {
    if (products.length === 0) {
      loadProducts(1, true);
    } else {
      loadProducts(currentPage + 1);
    }
  }, [loadProducts, products.length, currentPage]);

  const defaultLoadingComponent = (
    <div className="flex items-center justify-center py-8">
      <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      <span className="ml-2 text-gray-600">Đang tải sản phẩm...</span>
    </div>
  );

  const defaultErrorComponent = (error: string, retryFn: () => void) => (
    <div className="flex flex-col items-center justify-center py-8">
      <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
      <p className="text-red-600 mb-4 text-center">{error}</p>
      <button
        onClick={retryFn}
        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
        Try Again
      </button>
    </div>
  );

  if (initialLoad && loading) {
    return (
      <div className={`w-full ${containerClassName}`}>
        {loadingComponent || defaultLoadingComponent}
      </div>
    );
  }

  if (error && products.length === 0) {
    return (
      <div className={`w-full ${containerClassName}`}>
        {errorComponent
          ? errorComponent(error, retry)
          : defaultErrorComponent(error, retry)}
      </div>
    );
  }

  return (
    <div className={`w-full ${containerClassName} mt-[60px] `}>
      {/* Header */}
      <div className="text-center mb-2 md:mb-16">
        <div className="inline-flex items-center justify-center space-x-2 bg-blue-50 text-blue-600 px-4 py-2 rounded-full text-sm font-medium mb-6">
          <Sparkles className="w-4 h-4" />
          <span>Sản phẩm thuộc danh mục ({categoryName}) </span>
        </div>

        <h1 className="hidden md:block text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
          Sản Phẩm
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
            {" "}
            Ưa Chuộng
          </span>
        </h1>

        <p className="hidden md:block text-base sm:text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed mb-8">
          Khám phá những sản phẩm được khách hàng yêu thích và đánh giá cao — từ
          thiết kế hiện đại đến giải pháp nội thất thông minh.
        </p>

        <div className="hidden md:flex flex-wrap justify-center gap-8 mb-12">
          <div className="flex items-center space-x-2 text-gray-600">
            <div className="bg-green-100 p-2 rounded-full">
              <TrendingUp className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <div className="text-xl font-bold text-gray-900">150+</div>
              <div className="text-sm">Sản phẩm bán chạy</div>
            </div>
          </div>

          <div className="flex items-center space-x-2 text-gray-600">
            <div className="bg-blue-100 p-2 rounded-full">
              <Award className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <div className="text-xl font-bold text-gray-900">98%</div>
              <div className="text-sm">Khách hàng hài lòng</div>
            </div>
          </div>

          <div className="flex items-center space-x-2 text-gray-600">
            <div className="bg-purple-100 p-2 rounded-full">
              <Building2 className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <div className="text-xl font-bold text-gray-900">50+</div>
              <div className="text-sm">Danh mục sản phẩm</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Button */}
      {categoryName.includes("Sản phẩm") && (
        <div className="flex justify-end mb-2">
          <button
            onClick={handleOpenFilter}
            className={`inline-flex items-center gap-2 rounded-lg border transition-all duration-300 font-medium
        px-3 py-2 text-sm 
        sm:px-4 sm:py-2.5 sm:text-base
        ${
          hasActiveFilters
            ? "bg-blue-600 text-white border-blue-600 shadow-lg transform scale-105"
            : "bg-white text-gray-700 border-gray-300 hover:border-blue-600 hover:text-blue-600 hover:shadow-md"
        }`}>
            <Filter className="w-4 h-4" />
            <span className="hidden sm:inline">Bộ lọc </span>
            {hasActiveFilters && (
              <div className="bg-white text-blue-600 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold">
                {(filters.categoryTypeSlug !== "" ? 1 : 0) +
                  (filters.minPrice > 0 || filters.maxPrice < 10000000 ? 1 : 0)}
              </div>
            )}
          </button>
        </div>
      )}

      {/* Filter Drawer */}
      {isFilterOpen && (
        <>
          {/* Professional Blur Overlay */}
          <div
            className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40 transition-all duration-300 ease-out"
            onClick={handleCloseFilter}
          />

          {/* Drawer */}
          <div className="fixed right-0 top-0 h-[650px] w-80 bg-white/95 backdrop-blur-lg shadow-2xl z-50 transform transition-all duration-300 ease-out border-l border-white/20">
            <div className="flex flex-col h-full">
              {/* Header with Glass Effect */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200/50 bg-white/80 backdrop-blur-sm">
                <h2 className="text-lg font-semibold text-gray-900">
                  Bộ lọc sản phẩm
                </h2>
                <button
                  onClick={handleCloseFilter}
                  className="p-2 hover:bg-gray-100/80 rounded-lg transition-all duration-200 hover:scale-110">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Filter Content with Subtle Glass */}
              <div className="flex-1 p-6 overflow-y-auto bg-gradient-to-b from-white/90 to-white/95">
                {/* Price Range */}
                <div className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-1 h-6 bg-red-600 rounded-sm"></div>
                    <h3 className="text-base font-semibold text-gray-800">
                      KHOẢNG GIÁ
                    </h3>
                  </div>
                  <div className="space-y-3 text-sm">
                    <label className="flex items-center cursor-pointer group">
                      <input
                        type="radio"
                        name="priceRange"
                        value="0-1000000"
                        checked={selectedPriceRange === "0-1000000"}
                        onChange={(e) => handlePriceRangeChange(e.target.value)}
                        className="w-5 h-5 text-blue-600 border-gray-300 focus:ring-2 focus:ring-blue-500 cursor-pointer"
                      />
                      <span className="ml-3 text-gray-700 group-hover:text-gray-900">
                        Dưới 1 triệu
                      </span>
                    </label>

                    <label className="flex items-center cursor-pointer group">
                      <input
                        type="radio"
                        name="priceRange"
                        value="1000000-2000000"
                        checked={selectedPriceRange === "1000000-2000000"}
                        onChange={(e) => handlePriceRangeChange(e.target.value)}
                        className="w-5 h-5 text-blue-600 border-gray-300 focus:ring-2 focus:ring-blue-500 cursor-pointer"
                      />
                      <span className="ml-3 text-gray-700 group-hover:text-gray-900">
                        1 triệu - 2 triệu
                      </span>
                    </label>

                    <label className="flex items-center cursor-pointer group">
                      <input
                        type="radio"
                        name="priceRange"
                        value="2000000-5000000"
                        checked={selectedPriceRange === "2000000-5000000"}
                        onChange={(e) => handlePriceRangeChange(e.target.value)}
                        className="w-5 h-5 text-blue-600 border-gray-300 focus:ring-2 focus:ring-blue-500 cursor-pointer"
                      />
                      <span className="ml-3 text-gray-700 group-hover:text-gray-900">
                        2 triệu - 5 triệu
                      </span>
                    </label>

                    <label className="flex items-center cursor-pointer group">
                      <input
                        type="radio"
                        name="priceRange"
                        value="5000000-999999999"
                        checked={selectedPriceRange === "5000000-999999999"}
                        onChange={(e) => handlePriceRangeChange(e.target.value)}
                        className="w-5 h-5 text-blue-600 border-gray-300 focus:ring-2 focus:ring-blue-500 cursor-pointer"
                      />
                      <span className="ml-3 text-gray-700 group-hover:text-gray-900">
                        Trên 5 triệu
                      </span>
                    </label>
                  </div>
                </div>

                {/* Product Types - Single Selection */}
                <div className="mb-8">
                  <h3 className="text-sm font-semibold text-gray-900 mb-4">
                    Loại sản phẩm
                  </h3>
                  <div className="space-y-3">
                    {[
                      { value: "guitar-acoustic", label: "Guitar Acoustic" },
                      { value: "guitar-classic", label: "Guitar Classic" },
                      { value: "phu-kien", label: "Phụ kiện" },
                    ].map((type) => (
                      <label
                        key={type.value}
                        className="flex items-center cursor-pointer group">
                        <div className="relative">
                          <input
                            type="radio"
                            name="guitarType"
                            checked={
                              tempFilters.categoryTypeSlug === type.value
                            }
                            onChange={() => handleTypeSelect(type.value)}
                            className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500/50 transition-all duration-200"
                          />
                        </div>
                        <span className="ml-3 text-sm text-gray-700 group-hover:text-gray-900 transition-colors">
                          {type.label}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Footer with Glass Effect */}
              <div className="p-6 border-t border-gray-200/50 bg-white/90 backdrop-blur-sm">
                <div className="flex gap-3">
                  <button
                    onClick={handleClearFilters}
                    className="flex-1 py-3 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50/80 transition-all duration-200 font-medium hover:scale-105">
                    Xóa bộ lọc
                  </button>
                  <button
                    onClick={handleApplyFilters}
                    className="flex-1 py-3 px-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 font-medium shadow-lg hover:shadow-xl hover:scale-105">
                    Áp dụng
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Products */}
      <ProductWrapperCard hiddenGridButton={false} products={products} />

      {/* Loading More */}
      {loading && products.length > 0 && (
        <div className="mt-8">
          {loadingComponent || defaultLoadingComponent}
        </div>
      )}

      {/* Error While Loading More */}
      {error && products.length > 0 && (
        <div className="mt-8">
          {errorComponent
            ? errorComponent(error, retry)
            : defaultErrorComponent(error, retry)}
        </div>
      )}

      {/* No More Products */}
      {!hasMore && products.length > 0 && (
        <div className="flex items-center justify-center py-8">
          <p className="text-gray-500">Đã load hết sản phẩm</p>
        </div>
      )}

      {/* Empty State */}

      {/* Observer Target */}
      {hasMore && !error && <div ref={observerRef} className="h-4" />}
    </div>
  );
};
